import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSiteSession } from '../context/SiteSessionContext';
import { useAuth } from '../context/AuthContext';
import { getCollection, getSiteLabel, requestJson } from '../lib/api';

function getSiteLoginLabel(site) {
  if (!site) return '';
  return [site.code, getSiteLabel(site)].filter(Boolean).join(' - ');
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { setSession } = useSiteSession();
  const { account, isAuthenticated, login, logout } = useAuth();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteTab, setSiteTab] = useState('store');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    requestJson('/auth/sites')
      .then((payload) => setSites(getCollection(payload).filter((site) => site.active !== false)))
      .catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    if (isAuthenticated && account?.site) {
      setSelectedSite(account.site);
    }
  }, [account, isAuthenticated]);

  const connectAsSite = async (event) => {
    event?.preventDefault?.();
    if (!selectedSite) {
      setError('Sélectionne un site.');
      return;
    }
    if (!password) {
      setError('Saisis le mot de passe.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const payload = await login({ siteId: selectedSite._id, password });
      const site = payload.site || payload.account?.site || selectedSite;
      if (site.type === 'direction') {
        setSession({ mode: 'admin', label: 'Direction', site });
        navigate('/suivi-atelier');
      } else {
        setSession({ mode: 'site', site });
        navigate(site.type === 'sav' ? '/sav' : '/suivi-atelier');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredSites = useMemo(() => {
    if (siteTab === 'store') return sites.filter((site) => site.type === 'store');
    if (siteTab === 'workshop') return sites.filter((site) => site.type === 'workshop');
    if (siteTab === 'sav') return sites.filter((site) => site.type === 'sav');
    if (siteTab === 'direction') return sites.filter((site) => site.type === 'direction');
    return [];
  }, [sites, siteTab]);

  const selectedSiteTabLabel = siteTab === 'store' ? 'Magasin' : siteTab === 'workshop' ? 'Atelier' : siteTab === 'sav' ? 'Logistique' : 'Direction';
  const selectedSiteTabPlaceholder = siteTab === 'store' ? 'magasin' : siteTab === 'workshop' ? 'atelier' : siteTab === 'sav' ? 'site logistique' : 'accès direction';

  const handleTabChange = (_event, value) => {
    setSiteTab(value);
    setSelectedSite(null);
    setPassword('');
    setError('');
  };

  const handleAccountLogout = () => {
    logout();
    navigate('/auth');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        p: 3,
        background:
          'radial-gradient(circle at center, rgba(21, 94, 150, 0.18) 0%, rgba(3, 7, 13, 0) 48%)',
      }}
    >
      <Card
        variant="outlined"
        sx={{
          width: '100%',
          maxWidth: 560,
          borderColor: 'rgba(102,178,255,0.18)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.18)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={3}>
            <Stack alignItems="center" spacing={0.75} sx={{ pb: 1 }}>
              <Box
                component="img"
                src="/optibox-logo.svg"
                alt="Optibox"
                sx={{
                  width: 120,
                  height: 120,
                  objectFit: 'contain',
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 10, letterSpacing: '0.28em', textTransform: 'uppercase' }}
              >
                Optibox
              </Typography>
            </Stack>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 750 }}>
                Connexion
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choisissez un accès puis saisissez son mot de passe.
              </Typography>
            </Box>
            {isAuthenticated && (
              <Button type="button" variant="outlined" size="small" startIcon={<LogoutRoundedIcon />} onClick={handleAccountLogout}>
                Déconnecter la session actuelle
              </Button>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            <Tabs
              value={siteTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                minHeight: 40,
                '& .MuiTab-root': {
                  minHeight: 40,
                  fontWeight: 850,
                  textTransform: 'none',
                },
              }}
            >
              <Tab value="store" icon={<StoreRoundedIcon fontSize="small" />} iconPosition="start" label="Magasin" />
              <Tab
                value="workshop"
                icon={<ConstructionRoundedIcon fontSize="small" />}
                iconPosition="start"
                label="Atelier"
              />
              <Tab value="sav" icon={<SupportAgentRoundedIcon fontSize="small" />} iconPosition="start" label="Logistique" />
              <Tab
                value="direction"
                icon={<AdminPanelSettingsRoundedIcon fontSize="small" />}
                iconPosition="start"
                label="Direction"
              />
            </Tabs>

            <Box component="form" onSubmit={connectAsSite}>
              <Stack spacing={2}>
                <Autocomplete
                  options={filteredSites}
                  value={selectedSite}
                  onChange={(_event, value) => setSelectedSite(value)}
                  getOptionLabel={getSiteLoginLabel}
                  renderOption={(props, option) => (
                    <li {...props} key={option._id}>
                      <Stack direction="row" spacing={1.2} alignItems="center" sx={{ width: '100%' }}>
                        {option.type === 'store' ? <StoreRoundedIcon /> : option.type === 'sav' ? <SupportAgentRoundedIcon /> : option.type === 'direction' ? <AdminPanelSettingsRoundedIcon /> : <ConstructionRoundedIcon />}
                        <Box
                          component="span"
                          sx={{
                            px: 0.85,
                            py: 0.25,
                            minWidth: 64,
                            borderRadius: 999,
                            textAlign: 'center',
                            bgcolor: 'rgba(102, 178, 255, 0.14)',
                            color: 'primary.light',
                            border: '1px solid rgba(102, 178, 255, 0.24)',
                            fontSize: 12,
                            fontWeight: 950,
                            letterSpacing: '0.02em',
                            flexShrink: 0,
                          }}
                        >
                          {option.code}
                        </Box>
                        <Typography component="span" sx={{ flex: 1, minWidth: 0 }}>
                          {getSiteLabel(option)}
                        </Typography>
                        <Chip size="small" label={option.type === 'store' ? 'Magasin' : option.type === 'sav' ? 'Logistique' : option.type === 'direction' ? 'Direction' : 'Atelier'} />
                      </Stack>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={selectedSiteTabLabel}
                      placeholder={`Sélectionner un ${selectedSiteTabPlaceholder}`}
                    />
                  )}
                  noOptionsText={`Aucun ${selectedSiteTabPlaceholder} disponible`}
                  fullWidth
                />
                <TextField
                  label="Mot de passe"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                  fullWidth
                />
                <Button type="submit" variant="contained" size="large" disabled={loading}>
                  {loading ? 'Connexion…' : 'Se connecter'}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
